import { useTranslation } from 'react-i18next'
import { LocalizedLink } from './LocalizedLink'
import { Container } from './ui/Container'
import { Phone, Mail, MapPin } from 'lucide-react'

export function Footer() {
  const { t } = useTranslation()

  const navigationLinks = [
    { key: 'home', label: t('nav.home') },
    { key: 'about', label: t('nav.about') },
    { key: 'method', label: t('nav.method') },
    { key: 'products', label: t('nav.products') },
    { key: 'skills', label: t('nav.skills') },
    { key: 'careers', label: t('nav.careers') },
    { key: 'contacts', label: t('nav.contacts') },
  ]

  const legalLinks = [
    { key: 'privacy', label: t('footer.privacy') },
    { key: 'cookies', label: t('footer.cookiePolicy') },
  ]

  return (
    <footer className="bg-dark text-white">
      <Container>
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <LocalizedLink to="home" className="inline-flex bg-white rounded-md px-2 py-1 mb-6">
              <img
                src="/images/logos/simertec_logo.png"
                alt="Simertec"
                className="h-14 w-auto"
              />
            </LocalizedLink>
            <p className="text-gray-400 mb-6">{t('footer.tagline')}</p>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p>{t('footer.address')}</p>
                  <p>{t('footer.city')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <a href={`tel:${t('footer.phone').replace(/\s/g, '')}`} className="hover:text-primary transition-colors">
                  {t('footer.phone')}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                <a href={`mailto:${t('footer.email')}`} className="hover:text-primary transition-colors">
                  {t('footer.email')}
                </a>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-primary">{t('footer.navigation')}</h3>
            <ul className="space-y-3">
              {navigationLinks.map((link) => (
                <li key={link.key}>
                  <LocalizedLink
                    to={link.key}
                    className="text-gray-400 hover:text-white hover:underline transition-colors"
                  >
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-primary">{t('footer.legalNotes')}</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.key}>
                  <LocalizedLink
                    to={link.key}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-sm text-gray-500">
              <p>{t('footer.vat')}</p>
              <p>{t('footer.rea')}</p>
            </div>
          </div>

          {/* Certifications */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-primary">{t('footer.certifications')}</h3>
            <div className="flex gap-4">
              <img
                src="/images/certificates/certificata-1_658.png"
                alt="ISO 9001"
                className="h-16 w-auto"
              />
              <img
                src="/images/certificates/certificata2_659.png"
                alt={t('footer.certifications')}
                className="h-16 w-auto"
              />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10 text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} {t('footer.companyName')}. {t('footer.allRightsReserved')}</p>
        </div>
      </Container>
    </footer>
  )
}
